import { useState, useEffect } from 'react';
import './AdminList.css';
import { toast } from 'react-toastify';
import { authAPI } from '../../../config/auth-api';
import { categoryAPI } from '../../../config/category-api';
import { filterCategoriesByAccess } from '../../../utils/categoryAccessFilter';
import { Pencil, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import DeleteConfirmationModal from '../../Common/DeleteConfirmationModal/DeleteConfirmationModal';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { helpers } from '../../../utils/helpers';

const ALL_PERMISSION_VALUES = Object.values(ADMIN_PERMISSIONS);

const formatPermissionLabel = (perm) =>
  perm.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const AdminList = ({ adminData, setAdminData }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedAdminId, setSelectedAdminId] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNext: false,
    hasPrev: false
  });
  const [filters, setFilters] = useState({ search: '' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    profilePicture: '',
    profilePictureFile: null
  });

  // Permissions
  const [permissionAccessType, setPermissionAccessType] = useState('all');
  const [selectedPermissions, setSelectedPermissions] = useState([...ALL_PERMISSION_VALUES]);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [tempSelectedPermissions, setTempSelectedPermissions] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState('');

  // Category access
  const [categoryAccessType, setCategoryAccessType] = useState('all');
  const [categoryAccessSelection, setCategoryAccessSelection] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [tempSelectedCategories, setTempSelectedCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [categorySearch, setCategorySearch] = useState('');

  const canCreate = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.CREATE_ADMINS);
  const canEdit = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.EDIT_ADMINS);
  const canDelete = helpers.hasPermission(adminData, ADMIN_PERMISSIONS.DELETE_ADMINS);

  const [deleteModal, setDeleteModal] = useState({ isOpen: false, adminId: null, adminName: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchAdmins = async (page = 1, search = '') => {
    try {
      setAdminLoading(true);
      setAdminError(null);

      const response = await authAPI.getAdminsList(page, 20);

      if (response.success) {
        setAdmins(response.data);
        setPagination(response.pagination);
      } else {
        setAdminError(response.error || 'Failed to fetch admins');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setAdminError('Error fetching admins: ' + error.message);
    } finally {
      setAdminLoading(false);
    }
  };

  const revokePreviewUrl = (url) => {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setFormData(prev => {
      revokePreviewUrl(prev.profilePicture);
      return {
        ...prev,
        profilePicture: previewUrl,
        profilePictureFile: file
      };
    });
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryAPI.getCategories();

      if (response.success) {
        const transformedCategories = response.data.map((category) => ({
          id: category._id,
          title: category.title,
          image: category.image,
          subcategories: category.subCategories ? category.subCategories.map((sub) => ({
            id: sub._id,
            title: sub.title,
            image: sub.image,
          })) : []
        }));

        const uncategorizedOption = {
          id: 'uncategorized',
          title: 'Uncategorized',
          image: '',
          subcategories: []
        };

        const filtered = filterCategoriesByAccess([uncategorizedOption, ...transformedCategories]);
        setCategories(filtered);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchAdmins(currentPage, filters.search);
  }, [currentPage]);

  useEffect(() => {
    if ((isDialogOpen && (editMode || categoryAccessType === 'selected')) || isCategoryModalOpen) {
      if (categories.length === 0 && !loadingCategories) {
        fetchCategories();
      }
    }
  }, [isDialogOpen, editMode, isCategoryModalOpen, categoryAccessType, categories.length, loadingCategories]);

  useEffect(() => {
    if (categories.length > 0) {
      let filtered = categories.map(c => ({
        ...c,
        subcategories: c.subcategories ? [...c.subcategories] : []
      }));

      if (categorySearch.trim()) {
        const searchTerm = categorySearch.toLowerCase().trim();
        filtered = filtered.filter(category => {
          const categoryMatches = category.title.toLowerCase().includes(searchTerm);
          const subcategoryMatches = category.subcategories?.some(sub =>
            sub.title.toLowerCase().includes(searchTerm)
          );
          return categoryMatches || subcategoryMatches;
        });
      }

      filtered.forEach(category => {
        if (category.subcategories && category.subcategories.length > 0) {
          category.subcategories.sort((a, b) => {
            const categorySel = categoryAccessSelection.find(sel => sel.category === category.id);
            const aSelected = categorySel?.subcategories?.includes(a.id) ? 1 : 0;
            const bSelected = categorySel?.subcategories?.includes(b.id) ? 1 : 0;
            return bSelected - aSelected;
          });
        }
      });

      filtered.sort((a, b) => {
        const aSelected = categoryAccessSelection.some(sel => sel.category === a.id) ? 1 : 0;
        const bSelected = categoryAccessSelection.some(sel => sel.category === b.id) ? 1 : 0;
        return bSelected - aSelected;
      });

      setFilteredCategories(filtered);
    }
  }, [categories, categorySearch, categoryAccessSelection]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchAdmins(1, filters.search);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return true; // Phone is optional
    const cleanPhone = phone.replace(/\D/g, '');
    return /^\d{8}$/.test(cleanPhone);
  };

  const openPermissionsModal = () => {
    setTempSelectedPermissions([...selectedPermissions]);
    setPermissionSearch('');
    setIsPermissionsModalOpen(true);
  };

  const savePermissionsSelection = () => {
    setSelectedPermissions([...tempSelectedPermissions]);
    setIsPermissionsModalOpen(false);
  };

  const toggleTempPermission = (p) => {
    setTempSelectedPermissions((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const filteredPermissions = [...ALL_PERMISSION_VALUES].filter((p) => {
    const formatted = formatPermissionLabel(p).toLowerCase();
    const raw = p.toLowerCase();
    const query = permissionSearch.toLowerCase();
    return formatted.includes(query) || raw.includes(query);
  });

  const allTempSelected = filteredPermissions.length > 0 && filteredPermissions.every(p => tempSelectedPermissions.includes(p));

  const toggleAllPermissions = () => {
    if (allTempSelected) {
      setTempSelectedPermissions(prev => prev.filter(p => !filteredPermissions.includes(p)));
    } else {
      setTempSelectedPermissions(prev => [...new Set([...prev, ...filteredPermissions])]);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCategoryToggle = (id, isSub = false, parentId = null) => {
    setTempSelectedCategories(prev => {
      const category = isSub ? categories.find(c => c.id === parentId) : categories.find(c => c.id === id);
      if (!category) return prev;

      const subcatIds = category.subcategories?.map(s => s.id) || [];
      const existing = prev.find(p => p.category === category.id);

      if (!isSub) {
        if (existing) return prev.filter(p => p.category !== category.id);
        return [
          ...prev,
          { category: category.id, subcategories: [...subcatIds] }
        ];
      }

      if (!existing) {
        return [
          ...prev,
          { category: category.id, subcategories: [id] }
        ];
      }

      const updatedSubs = new Set(existing.subcategories);
      if (updatedSubs.has(id)) {
        updatedSubs.delete(id);
      } else {
        updatedSubs.add(id);
      }

      return prev.map(p =>
        p.category === category.id
          ? { ...p, subcategories: [...updatedSubs] }
          : p
      );
    });
  };

  const isCategorySelected = (categoryId) => {
    return tempSelectedCategories.some(p => p.category === categoryId);
  };

  const isSubcategorySelected = (subId, parentId) => {
    const found = tempSelectedCategories.find(p => p.category === parentId);
    if (!found) return false;
    return found.subcategories.includes(subId);
  };

  const openCategoryModal = () => {
    setTempSelectedCategories([...categoryAccessSelection]);
    setCategorySearch('');
    setExpandedCategories({});
    setIsCategoryModalOpen(true);
  };

  const saveCategorySelection = () => {
    setCategoryAccessSelection(tempSelectedCategories);
    setIsCategoryModalOpen(false);
  };

  const getSelectionSummary = () => {
    if (categoryAccessSelection.length === 0) return 'No categories selected';
    const catCount = categoryAccessSelection.length;
    const subCount = categoryAccessSelection.reduce((sum, c) => sum + (c.subcategories?.length || 0), 0);
    return `${catCount} categor${catCount === 1 ? 'y' : 'ies'}, ${subCount} subcategor${subCount === 1 ? 'y' : 'ies'}`;
  };

  const getSelectedCategoryNames = () => {
    if (!categoryAccessSelection || categoryAccessSelection.length === 0) {
      return {
        categories: [],
        subcategories: [],
        displayText: 'No categories selected'
      };
    }

    const displayItems = [];

    categoryAccessSelection.forEach(item => {
      const category = categories.find(c => c.id === item.category);
      if (!category) return;

      const subNames = (item.subcategories || []).map(subId => {
        const sub = category.subcategories?.find(s => s.id === subId);
        return sub ? sub.title : '';
      }).filter(Boolean);

      if (subNames.length > 0) {
        displayItems.push(`${category.title} (${subNames.join(', ')})`);
      } else {
        displayItems.push(category.title);
      }
    });

    return {
      displayText: displayItems.length > 0
        ? displayItems.map(item => item.toLowerCase()).map(item => item.replace(/\(/g, ' (')).join('\n')
        : 'No categories selected'
    };
  };

  const handleEdit = (admin) => {
    setEditMode(true);
    setSelectedAdminId(admin._id);
    setFormData({
      name: admin.name || '',
      email: admin.email || '',
      phoneNumber: admin.phoneNumber || '',
      profilePicture: admin.profilePicture || '',
      profilePictureFile: null
    });

    if (!admin.categoryAccess || admin.categoryAccess === 'all') {
      setCategoryAccessType('all');
      setCategoryAccessSelection([]);
    } else {
      setCategoryAccessType('selected');
      setCategoryAccessSelection(admin.categoryAccess);
    }

    let perms = [...ALL_PERMISSION_VALUES];
    if (admin.permissions?.length) {
      if (admin.permissions.includes('all')) {
        setPermissionAccessType('all');
        perms = [...ALL_PERMISSION_VALUES];
      } else {
        setPermissionAccessType('selected');
        const validPerms = new Set();
        admin.permissions.forEach(p => {
          if (ALL_PERMISSION_VALUES.includes(p)) {
            validPerms.add(p);
          }
        });
        perms = Array.from(validPerms);
      }
    } else {
      setPermissionAccessType('all');
    }

    setSelectedPermissions(perms);
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditMode(false);
    setSelectedAdminId(null);
    setFormData({
      name: '',
      email: '',
      phoneNumber: '',
      profilePicture: '',
      profilePictureFile: null
    });
    setCategoryAccessType('all');
    setCategoryAccessSelection([]);
    setPermissionAccessType('all');
    setSelectedPermissions([...ALL_PERMISSION_VALUES]);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    revokePreviewUrl(formData.profilePicture);
    setIsDialogOpen(false);
    setEditMode(false);
    setSelectedAdminId(null);
    setFormData({ name: '', email: '', phoneNumber: '', profilePicture: '', profilePictureFile: null });
    setCategoryAccessType('all');
    setCategoryAccessSelection([]);
    setPermissionAccessType('all');
    setSelectedPermissions([...ALL_PERMISSION_VALUES]);
    setCategories([]);
    setFilteredCategories([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.name.trim()) {
        toast.error('Name is required');
        setLoading(false);
        return;
      }

      if (!formData.email) {
        toast.error('Please enter a valid email address');
        setLoading(false);
        return;
      }

      const phone = formData.phoneNumber.trim();
      if (phone && !validatePhoneNumber(phone)) {
        toast.error('Phone number must be exactly 8 digits');
        setLoading(false);
        return;
      }

      if (categoryAccessType === 'selected' && categoryAccessSelection.length === 0) {
        toast.error('Please select at least one category');
        setLoading(false);
        return;
      }

      if (permissionAccessType === 'selected' && (!selectedPermissions || selectedPermissions.length === 0)) {
        toast.error('Please select at least one permission');
        setLoading(false);
        return;
      }

      let finalPermissions = selectedPermissions;
      if (permissionAccessType === 'all' || selectedPermissions.length === ALL_PERMISSION_VALUES.length) {
        finalPermissions = ['all'];
      }

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: phone || '',
        categoryAccess: categoryAccessType === 'all' ? 'all' : categoryAccessSelection,
        permissions: finalPermissions,
        profilePicture: formData.profilePicture,
        profilePictureFile: formData.profilePictureFile
      };

      let result;

      if (editMode) {
        result = await authAPI.updateAdmin(selectedAdminId, payload);
      } else {
        result = await authAPI.createAdmin(payload);
      }

      if (result.success) {
        toast.success(editMode ? 'Admin updated successfully!' : 'Admin created successfully!');

        if (editMode && selectedAdminId === adminData?._id && result.data) {
          const existing = authAPI.getAdminData();
          const updated = { ...existing, ...result.data };
          localStorage.setItem('adminData', JSON.stringify(updated));
          if (setAdminData) setAdminData(updated);
        }

        handleCloseDialog();
        fetchAdmins(currentPage, filters.search);
      } else {
        toast.error(result.error || (editMode ? 'Failed to update admin' : 'Failed to create admin'));
      }
    } catch (error) {
      console.error('Error submitting admin:', error);
      toast.error(`An error occurred while ${editMode ? 'updating' : 'creating'} the admin`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.adminId) return;

    try {
      setIsDeleting(true);
      const result = await authAPI.deleteAdmin(deleteModal.adminId);

      if (result.success) {
        toast.success(`Admin '${deleteModal.adminName}' deleted successfully`);
        setDeleteModal({ isOpen: false, adminId: null, adminName: '' });
        fetchAdmins(currentPage, filters.search);
      } else {
        toast.error(result.error || 'Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error('An error occurred while deleting the admin');
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (admin) => {
    setDeleteModal({
      isOpen: true,
      adminId: admin._id,
      adminName: admin.name
    });
  };

  return (
    <div className="adminlist-page">
      <div className="adminlist-page-header">
        <div className="adminlist-page-title">
          <h1>Admin Management</h1>
          <p>View and manage all administrators in the system</p>
        </div>
        {canCreate && (
          <button className="adminlist-create-btn" onClick={handleAddNew}>
            Add New Admin
          </button>
        )}
      </div>

      <div className="adminlist-table-container">
        {adminLoading ? (
          <div className="adminlist-loading-state">
            <div className="adminlist-loading-spinner"></div>
            <p>Loading admins...</p>
          </div>
        ) : adminError ? (
          <div className="adminlist-error-state">
            <p>{adminError}</p>
            <button
              onClick={() => fetchAdmins(currentPage, filters.search)}
              className="adminlist-retry-button"
            >
              Retry
            </button>
          </div>
        ) : admins.length === 0 ? (
          <div className="adminlist-empty-state">
            <p>No admins found</p>
          </div>
        ) : (
          <>
            <div className="adminlist-table-wrapper">
              <table className="adminlist-table">
                <thead>
                  <tr>
                    <th className="adminlist-pic-header">Pic</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Role</th>
                    <th>Permissions</th>
                    <th>Last Online</th>
                    {canEdit && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin._id}>
                      <td className="adminlist-pic-cell">
                        <div className="adminlist-table-pic-wrapper">
                          <img 
                            src={admin.profilePicture || '/images/default_admin_pic.jpeg'} 
                            alt={admin.name} 
                            className="adminlist-table-pic"
                          />
                        </div>
                      </td>
                      <td className="adminlist-name-cell">
                        <span className="adminlist-name-text">{admin.name}</span>
                      </td>
                      <td>{admin.email}</td>
                      <td>{admin.phoneNumber || '-'}</td>
                      <td><span className="adminlist-role-badge">{admin.role}</span></td>
                      <td>
                        <span className={`adminlist-permission-badge ${admin.permissions?.includes('all') ? 'all' : ''}`}>
                          {admin.permissions?.includes('all') 
                            ? 'All Permissions' 
                            : `${admin.permissions?.length || 0} Permission${admin.permissions?.length !== 1 ? 's' : ''}`}
                        </span>
                      </td>
                      <td>{admin.lastOnline || '-'}</td>
                      {(canEdit || canDelete) && (
                        <td>
                          <div className="adminlist-actions-cell">
                            {canEdit && (
                              <button
                                onClick={() => handleEdit(admin)}
                                className="adminlist-edit-btn"
                                title="Edit Admin"
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => openDeleteModal(admin)}
                                className="adminlist-delete-btn"
                                title="Delete Admin"
                                disabled={admin._id === adminData?._id}
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="adminlist-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={!pagination.hasPrev || adminLoading}
                  className="adminlist-pagination-button"
                >
                  Previous
                </button>

                <span className="adminlist-pagination-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={!pagination.hasNext || adminLoading}
                  className="adminlist-pagination-button"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {isDialogOpen && (
        <div className="adminlist-dialog-overlay">
          <div className="adminlist-dialog-box">
            <div className="adminlist-dialog-header">
              <div>
                <h2>{editMode ? 'Edit Admin' : 'Add New Admin'}</h2>
              </div>
              <button className="adminlist-close-btn" onClick={handleCloseDialog}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="adminlist-form">
              {editMode && (
                <div className="adminlist-profile-section">
                  <div className="adminlist-profile-upload-wrapper">
                    <div className="adminlist-profile-upload-container">
                      <div className="adminlist-profile-preview-container">
                        <img
                          src={formData.profilePicture || '/images/default_admin_pic.jpeg'}
                          alt={formData.name || 'Admin'}
                          className="adminlist-profile-upload-preview"
                        />
                      </div>
                      <label htmlFor="profilePicture" className="adminlist-profile-edit-badge" title="Upload Photo">
                        <Pencil size={14} />
                        <input
                          type="file"
                          id="profilePicture"
                          accept="image/*"
                          onChange={handleProfilePictureChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="adminlist-form-section">
                <div className="adminlist-form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter admin name"
                  />
                </div>

                <div className="adminlist-form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    disabled={editMode}
                  />
                </div>
              </div>

              <div className="adminlist-form-group">
                <label htmlFor="phoneNumber">Phone number <span className="adminlist-optional">(Optional)</span></label>
                <div className="adminlist-phone-input">
                  <span className="adminlist-country-code">+974</span>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter 8-digit phone number"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="adminlist-form-group">
                <label>Category</label>
                <div className="adminlist-radio-group">
                  <label className="adminlist-radio-label">
                    <input
                      type="radio"
                      name="categoryAccess"
                      value="all"
                      checked={categoryAccessType === 'all'}
                      onChange={() => {
                        setCategoryAccessType('all');
                        setCategoryAccessSelection([]);
                      }}
                    />
                    <span>All Categories</span>
                  </label>
                  <label className="adminlist-radio-label">
                    <input
                      type="radio"
                      name="categoryAccess"
                      value="selected"
                      checked={categoryAccessType === 'selected'}
                      onChange={() => setCategoryAccessType('selected')}
                    />
                    <span>Selected Categories</span>
                  </label>
                </div>

                {categoryAccessType === 'selected' && (
                  <div className="adminlist-category-access-section">
                    <div className="adminlist-choose-btn-container">
                      <button
                        type="button"
                        className="adminlist-choose-btn"
                        onClick={openCategoryModal}
                      >
                        Choose Categories
                      </button>
                    </div>
                    <div className="adminlist-selected-items">
                      <div className="adminlist-selected-categories-display">
                        {getSelectedCategoryNames().displayText.split('\n').map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Permissions */}
              <div className="adminlist-form-group">
                <label>Permissions</label>
                <div className="adminlist-radio-group">
                  <label className="adminlist-radio-label">
                    <input
                      type="radio"
                      name="permissionAccess"
                      value="all"
                      checked={permissionAccessType === 'all'}
                      onChange={() => {
                        setPermissionAccessType('all');
                        setSelectedPermissions([...ALL_PERMISSION_VALUES]);
                      }}
                    />
                    <span>All Permissions</span>
                  </label>
                  <label className="adminlist-radio-label">
                    <input
                      type="radio"
                      name="permissionAccess"
                      value="selected"
                      checked={permissionAccessType === 'selected'}
                      onChange={() => setPermissionAccessType('selected')}
                    />
                    <span>Selected Permissions</span>
                  </label>
                </div>

                {permissionAccessType === 'selected' && (
                  <div className="adminlist-category-access-section">
                    <div className="adminlist-choose-btn-container">
                      <button
                        type="button"
                        className="adminlist-choose-btn"
                        onClick={openPermissionsModal}
                      >
                        Choose Permissions
                      </button>
                    </div>
                    <div className="adminlist-selected-items">
                      <div className="adminlist-selected-categories-display">
                        <div>
                          {selectedPermissions.length} permission{selectedPermissions.length !== 1 ? 's' : ''} selected
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="adminlist-form-actions">
                <button
                  type="button"
                  className="adminlist-cancel-btn"
                  onClick={handleCloseDialog}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="adminlist-submit-btn"
                  disabled={loading}
                >
                  {loading ? (editMode ? 'Updating...' : 'Creating...') : (editMode ? 'Update Admin' : 'Create Admin')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, adminId: null, adminName: '' })}
        onConfirm={handleDelete}
        title="Delete Admin"
        message={`Are you sure you want to delete admin '${deleteModal.adminName}'? This action cannot be undone.`}
        isLoading={isDeleting}
      />

      {isPermissionsModalOpen && (
        <div className="adminlist-dialog-overlay" style={{ zIndex: 1001 }}>
          <div className="adminlist-dialog-box adminlist-perm-dialog">
            <div className="adminlist-dialog-header">
              <h2>Select Permissions</h2>
              <button className="adminlist-close-btn" onClick={() => setIsPermissionsModalOpen(false)}>&times;</button>
            </div>

            <div className="adminlist-cat-search-section">
              <input
                type="text"
                placeholder="Search permissions..."
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                className="adminlist-cat-search-input"
              />
            </div>

            <div className="adminlist-perm-modal-toolbar">
              <label className="adminlist-perm-select-all-label">
                <input
                  type="checkbox"
                  checked={allTempSelected}
                  onChange={toggleAllPermissions}
                />
                <span>Select All</span>
              </label>
              <span className="adminlist-perm-modal-count">
                {tempSelectedPermissions.length} / {ALL_PERMISSION_VALUES.length} selected
              </span>
            </div>

            <div className="adminlist-perm-modal-body">
              {filteredPermissions.length === 0 ? (
                <div className="adminlist-empty-state" style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  <p>No permissions found for "{permissionSearch}"</p>
                </div>
              ) : (
                <div className="adminlist-perm-modal-grid">
                  {filteredPermissions.sort((a, b) => {
                    const aSelected = selectedPermissions.includes(a) ? 1 : 0;
                    const bSelected = selectedPermissions.includes(b) ? 1 : 0;
                    return bSelected - aSelected;
                  }).map((p) => {
                    const active = tempSelectedPermissions.includes(p);
                    return (
                      <label
                        key={p}
                        className={`adminlist-perm-modal-card ${active ? 'adminlist-perm-modal-card--active' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleTempPermission(p)}
                        />
                        <span className="adminlist-perm-modal-label">{formatPermissionLabel(p)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="adminlist-cat-footer">
              <div className="adminlist-cat-selection-count"></div>
              <div className="adminlist-cat-footer-actions">
                <button type="button" className="adminlist-cancel-btn" onClick={() => setIsPermissionsModalOpen(false)}>Cancel</button>
                <button type="button" className="adminlist-submit-btn" onClick={savePermissionsSelection}>Save Selection</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCategoryModalOpen && (
        <div className="adminlist-dialog-overlay" style={{ zIndex: 1001 }}>
          <div className="adminlist-dialog-box adminlist-category-dialog">
            <div className="adminlist-dialog-header">
              <h2>Select Categories</h2>
              <button className="adminlist-close-btn" onClick={() => setIsCategoryModalOpen(false)}>
                &times;
              </button>
            </div>

            <div className="adminlist-cat-search-section">
              <input
                type="text"
                placeholder="Search categories and subcategories..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="adminlist-cat-search-input"
              />
            </div>

            <div className="adminlist-cat-content">
              {loadingCategories ? (
                <div className="adminlist-loading-state">
                  <div className="adminlist-loading-spinner"></div>
                  <p>Loading categories...</p>
                </div>
              ) : filteredCategories.length === 0 ? (
                <div className="adminlist-empty-state">
                  <p>{categorySearch ? `No categories found for "${categorySearch}"` : 'No categories found'}</p>
                </div>
              ) : (
                <div className="adminlist-cat-list">
                  {filteredCategories.map((category) => (
                    <div key={category.id} className="adminlist-cat-card">
                      <div className="adminlist-cat-header">
                        <label className="adminlist-cat-checkbox-label">
                          <input
                            type="checkbox"
                            checked={isCategorySelected(category.id)}
                            onChange={() => handleCategoryToggle(category.id)}
                          />
                          <div className="adminlist-cat-image">
                            {category.image ? (
                              <img src={category.image} alt={category.title} />
                            ) : (
                              <span className="adminlist-cat-no-image">No Image</span>
                            )}
                          </div>
                          <div className="adminlist-cat-info">
                            <h3 className="adminlist-cat-title">{category.title.charAt(0).toUpperCase() + category.title.slice(1)}</h3>
                          </div>
                        </label>

                        {category.subcategories && category.subcategories.length > 0 && (
                          <button
                            type="button"
                            className="adminlist-subcat-toggle-btn"
                            onClick={() => toggleCategory(category.id)}
                          >
                            <span className="adminlist-subcat-count-text">
                              {category.subcategories.length} subcategories
                            </span>
                            {expandedCategories[category.id] ?
                              <ChevronDown size={16} /> :
                              <ChevronRight size={16} />
                            }
                          </button>
                        )}
                      </div>

                      {expandedCategories[category.id] && category.subcategories && category.subcategories.length > 0 && (
                        <div className="adminlist-subcat-list">
                          {category.subcategories.map(subcategory => (
                            <label key={subcategory.id} className="adminlist-subcat-item">
                              <input
                                type="checkbox"
                                checked={isSubcategorySelected(subcategory.id, category.id)}
                                onChange={() => handleCategoryToggle(subcategory.id, true, category.id)}
                              />
                              <div className="adminlist-subcat-image">
                                {subcategory.image ? (
                                  <img src={subcategory.image} alt={subcategory.title} />
                                ) : (
                                  <span className="adminlist-cat-no-image">No Image</span>
                                )}
                              </div>
                              <div className="adminlist-subcat-info">
                                <h4 className="adminlist-subcat-title">{subcategory.title.charAt(0).toUpperCase() + subcategory.title.slice(1)}</h4>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="adminlist-cat-footer">
              <div className="adminlist-cat-selection-count">
                Selected: {tempSelectedCategories.length} items
              </div>
              <div className="adminlist-cat-footer-actions">
                <button
                  type="button"
                  className="adminlist-cancel-btn"
                  onClick={() => setIsCategoryModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="adminlist-submit-btn"
                  onClick={saveCategorySelection}
                >
                  Save Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminList;